// store/features/templates/templatesAPI.ts
import { baseApi } from '@/services/api';

// Get user role from Redux
const user = localStorage.getItem('authUser') ? JSON.parse(localStorage.getItem('authUser')!) : null;
const userRole = user?.userType || null;

export interface AITemplate {
  id: number;
  documentId: string;
  name: string;
  template_type: 'event' | 'email';
  industry: string;
  is_active: boolean;
  usage_count: number;
  description: string;
  preview_image: string | null;
  structure: EventStructure;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  isDeleted: boolean;
  deletedAt: string | null;
}

export interface EventStructure {
  title: string;
  subtitle: string;
  description: string;
  short_description: string;
  event_type: string;
  format: 'in_person' | 'virtual' | 'hybrid';
  industry: string;
  start_date: string;
  end_date: string;
  timezone: string;
  doors_open_at: string;
  registration_starts_at: string;
  registration_ends_at: string;
  early_bird_ends_at: string;
  location: {
    name: string;
    address: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
    latitude: number;
    longitude: number;
    virtual_link?: string;
  };
  ticketing_enabled: boolean;
  free_event: boolean;
  base_price: number;
  currency: string;
  capacity: number;
  min_attendees: number;
  registered_count: number;
  waitlist_enabled: boolean;
  organizer_name: string;
  organizer_email: string;
  organizer_phone: string;
  organizer_website: string;
  images: Array<{
    url: string;
    alt: string;
  }>;
  tags: string[];
  topics: string[];
  featured: boolean;
  visibility: 'public' | 'private' | 'unlisted';
  send_confirmations: boolean;
  send_reminders: boolean;
  allow_comments: boolean;
  allow_reviews: boolean;
  ai_generated: boolean;
  ai_model: string;
  industry_data: Record<string, any>;
}

export interface TemplatesResponse {
  data: AITemplate[];
  meta: {
    pagination: {
      page: number;
      pageSize: number;
      pageCount: number;
      total: number;
    };
  };
}

export interface GetTemplatesParams {
  page?: number;
  pageSize?: number;
  industry?: string;
  template_type?: 'event' | 'email';
  is_active?: boolean;
  search?: string;
  sort?: string;
}

export interface GenerateFromTemplateRequest {
  templateId: string;
  customizations: {
    title?: string;
    description?: string;
    start_date?: string;
    end_date?: string;
    location?: Partial<EventStructure['location']>;
    base_price?: number;
    capacity?: number;
    [key: string]: any;
  };
  userPrompt?: string;
  model?: 'openai' | 'gemini' | 'claude';
}

export const templatesAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ✅ Get All Templates with Pagination & Filters
    getTemplates: builder.query<TemplatesResponse, any>({
      query: (params = {}) => {
        const queryParams = new URLSearchParams();
        
        if (params.page) queryParams.append('pagination[page]', params.page.toString());
        if (params.pageSize) queryParams.append('pagination[pageSize]', params.pageSize.toString());
        if (params.industry) queryParams.append('filters[industry][$eq]', params.industry);
        if (params.template_type) queryParams.append('filters[template_type][$eq]', params.template_type);
        if (params.is_active !== undefined) queryParams.append('filters[is_active][$eq]', params.is_active.toString());
        // if (params.search) queryParams.append('filters[name][$contains]', params.search);
        if (params.sort) queryParams.append('sort', params.sort);
        
        return {
          url: `/ai-templates?${params}`,
          method: 'GET',
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ documentId }) => ({
                type: 'AITemplates' as const,
                id: documentId,
              })),
              { type: 'AITemplates', id: 'LIST' },
            ]
          : [{ type: 'AITemplates', id: 'LIST' }],
    }),

    // ✅ Get Single Template by ID or documentId
    getTemplate: builder.query<{ data: AITemplate }, string>({
      query: (id) => ({
        url: `/ai-templates/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'AITemplates', id }],
    }),

    // ✅ Get Templates by Industry
    getTemplatesByIndustry: builder.query<TemplatesResponse, string>({
      query: (industry) => ({
        url: `/ai-templates?filters[industry][$eq]=${industry}&filters[is_active][$eq]=true`,
        method: 'GET',
      }),
      providesTags: ['AITemplates'],
    }),

    // ✅ Generate Event from Template (AI-powered customization)
    generateFromTemplate: builder.mutation<any, GenerateFromTemplateRequest>({
      query: (data) => ({
        url: `/custom/ai/event/agent/setup/call/secure/${userRole}/generate-from-template`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['AIGenerations', 'Events'],
    }),

    // ✅ Update Template Usage Count
    incrementTemplateUsage: builder.mutation<void, string>({
      query: (templateId) => ({
        url: `/ai-templates/${templateId}/increment-usage`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'AITemplates', id }],
    }),

    // ✅ Search Templates
    searchTemplates: builder.query<TemplatesResponse, string>({
      query: (searchTerm) => ({
        url: `/ai-templates?filters[name][$contains]=${searchTerm}&filters[is_active][$eq]=true`,
        method: 'GET',
      }),
      providesTags: ['AITemplates'],
    }),
  }),
});

export const {
  useGetTemplatesQuery,
  useGetTemplateQuery,
  useGetTemplatesByIndustryQuery,
  useGenerateFromTemplateMutation,
  useIncrementTemplateUsageMutation,
  useSearchTemplatesQuery,
  useLazyGetTemplatesQuery,
  useLazySearchTemplatesQuery,
} = templatesAPI;
